// API client — base URL configured via Vite env var.
// In dev: VITE_API_BASE defaults to "/api" with a Vite proxy to the real API.
// In prod: VITE_API_BASE is the CloudFront domain (/api routed to API Gateway).

const BASE = import.meta.env.VITE_API_BASE ?? '/api';

interface ApiBody<T> {
  data: T | null;
  error: string | null;
  message: string | null;
}

export class ApiError extends Error {
  constructor(public status: number, public code: string, message?: string | null) {
    super(message ?? code);
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = (await res.json()) as ApiBody<T>;
  if (!res.ok || json.error) {
    throw new ApiError(res.status, json.error ?? 'unknown', json.message);
  }
  if (json.data === null) {
    throw new ApiError(res.status, 'empty_data');
  }
  return json.data;
}

export const api = {
  health: () => request<{ ok: boolean; service: string; ts: number }>('GET', '/health'),

  // Topics
  createTopic: (input: {
    topic: string;
    length_min: '6' | '8' | '10' | '12' | '15';
    tone: string;
    reference_channels?: string[];
  }) => request<TopicRecord>('POST', '/topics', input),
  listTopics: () =>
    request<{ items: TopicRecord[]; count: number }>('GET', '/topics'),
  getTopic: (id: string) => request<TopicRecord>('GET', `/topics/${id}`),

  // Scripts
  listAwaitingApproval: () =>
    request<{ items: TopicRecord[]; count: number }>(
      'GET',
      '/scripts/awaiting-approval',
    ),
  getScript: (script_id: string, version: string) =>
    request<ScriptRecord>('GET', `/scripts/${script_id}/${version}`),
  approveScript: (input: {
    task_token: string;
    script_id: string;
    script_version: string;
  }) => request<{ approved: boolean }>('POST', '/scripts/approve', input),
  rejectScript: (input: { task_token: string; reason: string }) =>
    request<{ rejected: boolean }>('POST', '/scripts/reject', input),

  // Videos
  getVideo: (video_id: string) => request<VideoRecord>('GET', `/videos/${video_id}`),
  getVideoAudio: (video_id: string) =>
    request<VideoAudioResponse>('GET', `/videos/${video_id}/audio`),
  listVideosByStatus: (status: string) =>
    request<{ items: VideoRecord[]; count: number }>(
      'GET',
      `/videos?status=${encodeURIComponent(status)}`,
    ),
  listVideosAwaitingApproval: () =>
    request<{ items: VideoRecord[]; count: number }>('GET', '/videos/awaiting-approval'),
  approveVideo: (input: {
    task_token: string;
    video_id: string;
    selected_title: string;
    selected_thumbnail_key: string;
    publish_at_ms?: number | null;
  }) => request<{ approved: boolean }>('POST', '/videos/approve', input),
  rejectVideo: (input: { task_token: string; reason: string }) =>
    request<{ rejected: boolean }>('POST', '/videos/reject', input),

  // Executions
  listExecutions: (status?: string, limit?: number) => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (limit) params.set('limit', String(limit));
    const qs = params.toString();
    return request<{ items: ExecutionListItem[]; count: number }>(
      'GET',
      `/executions${qs ? `?${qs}` : ''}`,
    );
  },
  getExecution: (execution_arn: string) =>
    request<ExecutionDetail>('GET', `/executions/${encodeURIComponent(execution_arn)}`),

  // Costs
  costSummary: () => request<CostSummary>('GET', '/costs/summary'),
};

export interface VideoAudioResponse {
  video_id: string;
  narration_url: string;
  alignment_url: string;
  narration_duration_sec: number | null;
  chapter_audio_keys: string[];
  expires_in_sec: number;
}

export interface TopicRecord {
  topic_id: string;
  video_id: string;
  topic: string;
  length_min: string;
  tone: string;
  status: string;
  safety_check_result?: {
    safe: boolean;
    reasoning: string;
    suggested_reframe_if_unsafe?: string;
    confidence: number;
  };
  script_id?: string;
  script_version?: string;
  script_approval_task_token?: string;
  research_key?: string;
  created_at: number;
  updated_at: number;
}

export interface ScriptRecord {
  script_id: string;
  topic_id: string;
  version: string;
  script_text: string;
  chapters: Array<{
    id: string;
    title: string;
    start_estimate_sec: number;
    text: string;
  }>;
  b_roll_callouts: Array<{
    id: string;
    timestamp_sec: number;
    duration_sec: number;
    visual_description: string;
    type: 'stock' | 'generated' | 'chart' | 'text_overlay';
    aspect_ratio: '16:9' | '9:16';
  }>;
  title_options: string[];
  thumbnail_concepts: Array<{ copy: string; visual_description: string }>;
  description: string;
  created_at: number;
}

export interface VideoRecord {
  video_id: string;
  topic_id?: string;
  script_id?: string;
  status: string;
  selected_title?: string;
  selected_thumbnail_key?: string;
  thumbnail_keys?: Record<string, string>;
  thumbnail_urls?: Record<string, string>;
  title_options?: string[];
  thumbnail_concepts?: Array<{ copy: string; visual_description: string }>;
  description?: string;
  final_key?: string;
  final_url?: string | null;
  youtube_video_id?: string;
  youtube_url?: string;
  video_approval_task_token?: string;
  created_at?: number;
  updated_at?: number;
  expires_in_sec?: number;
}

export interface ExecutionListItem {
  execution_arn: string;
  name: string;
  status: 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'TIMED_OUT' | 'ABORTED';
  start_date: number | null;
  stop_date: number | null;
}

export interface ExecutionDetail extends ExecutionListItem {
  input?: string;
  output?: string;
  error?: string | null;
  cause?: string | null;
  events: Array<{
    id?: string;
    type?: string;
    timestamp: number | null;
    state_entered?: string;
    state_exited?: string;
    task_succeeded?: string;
    task_failed?: string;
    lambda_failed?: string;
  }>;
}

export interface CostSummary {
  window_days: number;
  daily: Array<{ day: string; total_usd: number; over_cap: boolean }>;
  steps: Array<{ step_name: string; total_usd: number; rows: number }>;
  anomalies: Array<{ video_id: string; total_usd: number }>;
  totals: {
    last_30d_usd: number;
    daily_cap_usd: number;
    per_video_cap_usd: number;
  };
}
